import { useNavigate } from "react-router-dom";
import { featureFlags } from "../../config/featureFlags";
import { useComplianceWorkflowIndex, isWorkflowActiveStage } from "../../contexts/ComplianceWorkflowIndexContext";
import { useI18n } from "../../i18n";
import type { EffectiveStandardAccess } from "../../services/standardAccessApi";
import { Button } from "../ui/Button";
import { normalizeComplianceStandardCode } from "../../utils/complianceStandard";
import { isStandardGrantedInEffective } from "../../utils/standardAccessPolicy";

export type OperationalWorkflowButtonProps = {
  standardCode: string;
  effectiveAccess?: EffectiveStandardAccess | null;
  size?: "small" | "medium";
  className?: string;
  color?: "primary" | "outline" | "secondary";
};

export function operationalWorkflowPath(standardCode: string): string {
  const code = normalizeComplianceStandardCode(standardCode);
  return `/compliance-workflow/${encodeURIComponent(code)}`;
}

export default function OperationalWorkflowButton({
  standardCode,
  effectiveAccess = null,
  size = "small",
  className,
  color = "outline",
}: OperationalWorkflowButtonProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { workflowStageByStandard } = useComplianceWorkflowIndex();

  if (!featureFlags.COMPLIANCE_WORKFLOW_V1) {
    return null;
  }

  const code = normalizeComplianceStandardCode(standardCode);
  if (!code || code === "ALL") {
    return null;
  }

  const granted = isStandardGrantedInEffective(effectiveAccess, code);
  const stage = workflowStageByStandard.get(code);
  const active = isWorkflowActiveStage(stage);

  const label = active
    ? t("complianceWorkflow.openWorkflow", "Open workflow")
    : t("complianceWorkflow.startWorkflow", "Start workflow");

  const tooltip = !granted
    ? t("complianceWorkflow.notGrantedTooltip", "This standard is not granted for your organization or user.")
    : undefined;

  return (
    <Button
      type="button"
      size={size}
      color={color}
      className={className}
      disabled={!granted}
      title={tooltip}
      aria-label={t("complianceWorkflow.openOperationalWorkflow", "Open operational workflow")}
      data-testid={`operational-workflow-button-${code}`}
      onClick={() => {
        if (!granted) return;
        navigate(operationalWorkflowPath(code));
      }}
    >
      {label}
    </Button>
  );
}
